import {
  createRouter,
  createWebHistory,
  type RouteLocationNormalized,
  type RouteRecordRaw,
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import {
  AB_QUERY_PARAM,
  isValidVariant,
  resolveVariant,
  setVariant,
  trackAssignment,
} from '@/lib/ab'

const AppShell = () => import('@/layouts/AppShell.vue')

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: 'landing',
    component: () => import('@/views/LandingView.vue'),
    meta: { layout: 'public', title: 'MWS AI' },
  },
  {
    path: '/app',
    component: AppShell,
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        redirect: { name: 'code-agents-hub' },
      },
      {
        path: 'workspace',
        name: 'code-agents-hub',
        component: () => import('@/views/CodeAgentsHubView.vue'),
        meta: { title: 'Задачи' },
      },
      {
        path: 'code-agents',
        name: 'code-agents',
        component: () => import('@/views/CodeAgentsView.vue'),
        meta: { title: 'Задачи · Inbox', abVariant: 'a' },
      },
      {
        path: 'code-agents-v2',
        name: 'code-agents-v2',
        component: () => import('@/views/CodeAgentsViewV2.vue'),
        meta: { title: 'Задачи · Workspace', abVariant: 'b' },
      },
      {
        path: 'ab-stats',
        name: 'ab-stats',
        component: () => import('@/views/AbStatsView.vue'),
        meta: { title: 'A/B статистика' },
      },
      {
        path: 'billing',
        name: 'billing',
        component: () => import('@/views/BillingView.vue'),
        meta: { title: 'Биллинг' },
      },
      {
        path: 'profile',
        name: 'profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: { title: 'Профиль' },
      },
    ],
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: () => import('@/views/NotFoundView.vue'),
    meta: { layout: 'public', title: 'Страница не найдена' },
  },
]

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: () => ({ top: 0 }),
})

router.beforeEach(async (to: RouteLocationNormalized) => {
  const auth = useAuthStore()
  if (!auth.bootstrapped) {
    await auth.bootstrap()
  }
  if (to.meta.requiresAuth && !auth.isAuthenticated) {
    return {
      name: 'landing',
      query: { return_to: to.fullPath },
    }
  }
  if (to.name === 'landing' && auth.isAuthenticated) {
    return { name: 'code-agents-hub' }
  }

  // Sticky variant when we land on either variant with `?v=a/b`.
  const explicit = to.query[AB_QUERY_PARAM]
  if (typeof explicit === 'string' && isValidVariant(explicit.toLowerCase())) {
    setVariant(explicit.toLowerCase() as 'a' | 'b')
  }
})

router.afterEach((to) => {
  const title = (to.meta.title as string | undefined) || 'MWS AI'
  document.title = to.name === 'landing' ? title : `${title} — MWS AI`
  const abVariant = to.meta.abVariant as 'a' | 'b' | undefined
  if (abVariant) {
    trackAssignment(resolveVariant())
  }
})
